import { Controller } from '@hotwired/stimulus'

const syncDelay = 500

export default class OptimisticLikeController extends Controller {
  static targets = ['count', 'icon', 'link']

  static values = {
    liked: Boolean,
    count: Number,
    likedIcon: String,
    unlikedIcon: String,
    likeUrl: String,
    unlikeUrl: String,
    reaction: String
  }

  connect() {
    this.confirmedLiked = this.likedValue
    this.syncing = false
    this.syncTimer = null
    this.handlePageHide = this.flushPendingState.bind(this)
    globalThis.addEventListener('pagehide', this.handlePageHide)
  }

  toggle(event) {
    event.preventDefault()

    const liked = !this.likedValue
    if (liked) this.clearOppositeReaction()
    this.renderState(liked)
    this.scheduleSync()
  }

  clearOppositeReaction() {
    const reactionGroup = this.element.closest('.post-option-button')
    if (!reactionGroup) return

    this.clearedOppositeControllers = []
    reactionGroup
      .querySelectorAll('[data-controller~="optimistic-like"]')
      .forEach(element => {
        if (element === this.element) return

        const controller = this.application.getControllerForElementAndIdentifier(
          element,
          'optimistic-like'
        )
        if (!controller?.likedValue) return
        if (controller.reactionValue === this.reactionValue) return

        controller.renderState(false)
        this.clearedOppositeControllers.push(controller)
      })
  }

  scheduleSync() {
    clearTimeout(this.syncTimer)
    this.syncTimer = null

    if (this.syncing || this.confirmedLiked === this.likedValue) return

    this.syncTimer = setTimeout(() => {
      this.syncTimer = null
      this.syncState()
    }, syncDelay)
  }

  async syncState() {
    if (this.syncing || this.confirmedLiked === this.likedValue) return

    this.syncing = true
    const requestedState = this.likedValue

    try {
      const stream = await this.persistState(requestedState)
      this.confirmedLiked = requestedState

      // Do not render an older response over clicks made while it was running.
      if (this.likedValue === requestedState) {
        Turbo.renderStreamMessage(stream)
      }
      this.clearedOppositeControllers = []
    } catch (error) {
      if (this.likedValue !== this.confirmedLiked) {
        this.renderState(this.confirmedLiked)
      }
      this.restoreOppositeReactions()
      console.error('Unable to update like:', error)
    } finally {
      this.syncing = false

      if (this.confirmedLiked !== this.likedValue) this.scheduleSync()
    }
  }

  restoreOppositeReactions() {
    this.clearedOppositeControllers?.forEach(controller => {
      if (controller.confirmedLiked && !controller.likedValue) {
        controller.renderState(true)
      }
    })
    this.clearedOppositeControllers = []
  }

  disconnect() {
    clearTimeout(this.syncTimer)
    this.syncTimer = null
    globalThis.removeEventListener('pagehide', this.handlePageHide)
  }

  flushPendingState() {
    clearTimeout(this.syncTimer)
    this.syncTimer = null

    // An active request already uses keepalive. Only flush a state that is
    // still waiting for the debounce timer.
    if (this.syncing || this.confirmedLiked === this.likedValue) return

    const requestedState = this.likedValue
    this.confirmedLiked = requestedState

    this.persistState(requestedState).catch(error => {
      console.error('Unable to update like before leaving page:', error)
    })
  }

  async persistState(liked) {
    const response = await fetch(
      liked ? this.likeUrlValue : this.unlikeUrlValue,
      {
        method: liked ? 'POST' : 'DELETE',
        headers: {
          Accept: 'text/vnd.turbo-stream.html',
          'X-CSRF-Token': document.querySelector(
            'meta[name="csrf-token"]'
          )?.content || '',
          'X-Requested-With': 'XMLHttpRequest'
        },
        credentials: 'same-origin',
        keepalive: true
      }
    )

    if (!response.ok) {
      throw new Error(`Like request failed with status ${response.status}`)
    }

    if (response.redirected) throw new Error('Like request was redirected')

    const stream = await response.text()
    if (!stream.trim()) throw new Error('Like request returned no update')

    return stream
  }

  renderState(liked) {
    this.countValue = Math.max(
      0,
      this.countValue + (liked ? 1 : -1)
    )
    this.likedValue = liked
    this.updateElements()
  }

  updateElements() {
    this.countElements.forEach(element => {
      element.textContent = this.countValue
    })

    if (this.hasIconTarget) {
      this.iconTarget.src = this.likedValue
        ? this.likedIconValue
        : this.unlikedIconValue
    }

    this.linkTarget.setAttribute('aria-pressed', String(this.likedValue))
  }
  get countElements() {
    return this.element.parentElement?.querySelectorAll(
      '[data-optimistic-like-target="count"], .count-target'
    ) || []
  }
}
